import { FC, useMemo } from 'react';

import { Link } from 'react-router-dom';

import { ColumnDef, Row } from '@tanstack/react-table';
import { formatCurrency, formatPercent } from 'configs/columns';
import { DataDisplayTable } from 'ui-kit';

import { SaleOfferings } from 'query/__generated__/sales';

import { salePoolValuationColumnGroup, useSalePoolValuations } from './salePoolValuations';
import { formatCalculationDate } from './salesUtils';

type SaleOffering = SaleOfferings['saleOfferings'][number];
// DataDisplayTable's row type requires last_updated_date; SaleOfferingView has no such field, so
// this is an unused placeholder to satisfy the shared component's contract (same pattern as
// SalesIndex's SaleRow).
type SaleOfferingRow = SaleOffering & { last_updated_date: string };

export const poolLoansPath = (saleId: string, offeringId: string) =>
  `/sales/${saleId}/pools/${offeringId}/loans`;

// The pool's name links to its page, so the keyboard reaches it and a text selection doesn't
// navigate.
const buildColumns = (
  saleId: string,
  calculationDate: string,
): ColumnDef<SaleOfferingRow, any>[] => [
  {
    accessorKey: 'offering_name',
    header: 'Pool',
    cell: (props) => (
      <Link
        to={poolLoansPath(saleId, props.row.original.offering_id)}
        className="font-medium text-purple-300 hover:underline light:text-purple-600"
      >
        {props.row.original.offering_name}
      </Link>
    ),
    meta: { numeric: false, alignment: 'left' },
  },
  {
    accessorKey: 'fdic_pool_number',
    header: 'FDIC Pool #',
    meta: { numeric: false, alignment: 'left' },
  },
  {
    accessorKey: 'loan_count',
    header: 'Loans',
    meta: { numeric: true, alignment: 'left' },
  },
  {
    accessorKey: 'current_balance_cents',
    header: `Book Value as of ${formatCalculationDate(calculationDate)}`,
    cell: (props) => <span>{formatCurrency(props.row.original.current_balance_cents)}</span>,
    meta: { numeric: true, alignment: 'left' },
  },
  {
    accessorKey: 'wa_coupon_bps',
    header: 'WA Coupon',
    cell: (props) => <span>{formatPercent(props.row.original.wa_coupon_bps, 3)}</span>,
    meta: { numeric: true, alignment: 'left' },
  },
  {
    accessorKey: 'wa_ltv_bps',
    header: 'WA LTV',
    cell: (props) => <span>{formatPercent(props.row.original.wa_ltv_bps, 1)}</span>,
    meta: { numeric: true, alignment: 'left' },
  },
];

const getPoolRowProps = (row: Row<SaleOfferingRow>) =>
  ({
    'data-testid': 'sale-pool-row',
    'data-offering-id': row.original.offering_id,
    'data-fdic-pool-number': row.original.fdic_pool_number,
  }) as React.HTMLAttributes<HTMLTableRowElement>;

const PoolsTable: FC<{
  offerings: SaleOffering[];
  columns: ColumnDef<SaleOfferingRow, any>[];
}> = ({ offerings, columns }) => {
  const rows: SaleOfferingRow[] = offerings.map((offering) => ({
    ...offering,
    last_updated_date: '',
  }));

  return (
    <DataDisplayTable
      data={rows}
      columns={columns}
      noDataMessage="No pools in this sale yet."
      getRowId={(offering) => offering.offering_id}
      stylingOptions={{ hoverRowColor: 'gray', supportsLightTheme: true }}
      getRowProps={getPoolRowProps}
      columnPinningLeft={['offering_name']}
    />
  );
};

const SellerPoolsTable: FC<{
  saleId: string;
  offerings: SaleOffering[];
  poolColumns: ColumnDef<SaleOfferingRow, any>[];
}> = ({ saleId, offerings, poolColumns }) => {
  const valuations = useSalePoolValuations(saleId);
  const sellerColumns = useMemo(
    () => [...poolColumns, salePoolValuationColumnGroup<SaleOfferingRow>(valuations)],
    [poolColumns, valuations],
  );
  return <PoolsTable offerings={offerings} columns={sellerColumns} />;
};

// A bidder's table neither has the valuation columns nor sends their query.
export const SaleOfferingsTable: FC<{
  saleId: string;
  offerings: SaleOffering[];
  isSeller: boolean;
  calculationDate: string;
}> = ({ saleId, offerings, isSeller, calculationDate }) => {
  const columns = useMemo(() => buildColumns(saleId, calculationDate), [saleId, calculationDate]);
  return isSeller ? (
    <SellerPoolsTable saleId={saleId} offerings={offerings} poolColumns={columns} />
  ) : (
    <PoolsTable offerings={offerings} columns={columns} />
  );
};
